import React, { useState } from "react";
import { IDefaultPackage, IPackages } from "@/interfaces";
import { cn, formatNumber } from "@/lib";
import styles from "@/components/Payment/paymentCheckout.module.css";

interface IRadioProps {
  packages: IPackages[];
  defaultValue: IDefaultPackage;
  selectedPackage: IDefaultPackage | IPackages | null;
  setSelectedPackage: (pkg: IPackages | IDefaultPackage) => void;
}

const packageThemes = [
  { plan: "#54b8f1", background: "#f2faff" },
  { plan: "#4ccf8a", background: "#f3fff7" },
  { plan: "#faa763", background: "#fff9f1" },
  { plan: "#ff859c", background: "#fff5f7" },
];

const RadioPackageGroup: React.FC<IRadioProps> = ({
  packages,
  defaultValue,
  selectedPackage,
  setSelectedPackage,
}) => {
  const [isPackagesOpen, setIsPackagesOpen] = useState(false);
  const hasSelectedPackage = selectedPackage?.id !== "p0";
  const highestDiscount = Math.max(
    0,
    ...packages.map((pkg) => Number(pkg.discount?.match(/\d+/)?.[0] || 0)),
  );

  return (
    <form onSubmit={(event) => event.preventDefault()} className="w-full">
      <div role="radiogroup" aria-label="انتخاب بسته‌های استعلام">
        {hasSelectedPackage && !isPackagesOpen && (
          <button
            className={styles.resetOrder}
            type="button"
            onClick={() => setSelectedPackage(defaultValue)}
          >
            فقط یک استعلام می‌خواهم
          </button>
        )}
        <button
          className={styles.promo}
          type="button"
          aria-expanded={isPackagesOpen}
          onClick={() => setIsPackagesOpen((current) => !current)}
        >
          <div>
            <div className={styles.promoTitle}>
              {highestDiscount > 0 && (
                <span className={styles.discountHighlight}>
                  تا {highestDiscount}٪ تخفیف
                </span>
              )}{" "}
              برای استعلام‌های بیشتر
            </div>
            <div className={styles.promoDescription}>
              بسته‌های مخصوص {defaultValue.name}
            </div>
          </div>
          <span className={styles.promoArrow}>
            {isPackagesOpen ? "بستن" : "مشاهده"}
          </span>
        </button>

        {isPackagesOpen && (
          <div className={styles.packages}>
            <div className={styles.packagesHead}>
              <span>انتخاب بسته، مخصوص همین سرویس</span>
              <span>قیمت به تومان</span>
            </div>
            <div className={styles.packageGrid}>
              {packages.map((pkg, index) => {
                const selected = selectedPackage?.id === pkg.id;
                const theme = packageThemes[index % packageThemes.length];
                const packageStyle: React.CSSProperties &
                  Record<"--plan" | "--plan-bg", string> = {
                  "--plan": theme.plan,
                  "--plan-bg": theme.background,
                };

                return (
                  <label
                    key={pkg.id}
                    className="block cursor-pointer"
                    htmlFor={`package-${pkg.id}`}
                  >
                    <input
                      id={`package-${pkg.id}`}
                      className="sr-only"
                      type="radio"
                      name="package-selection"
                      checked={selected}
                      onChange={() => {
                        setSelectedPackage(pkg);
                        setIsPackagesOpen(false);
                      }}
                    />
                    <span
                      className={cn(
                        styles.package,
                        selected && styles.packageSelected,
                      )}
                      style={packageStyle}
                    >
                      <span className={styles.packageTop}>
                        <span className={styles.packageTitle}>
                          <strong>{pkg.title}</strong>
                          <small>{pkg.services[0] || defaultValue.name}</small>
                        </span>
                        {pkg.discount && (
                          <span className={styles.discount}>
                            {pkg.discount}
                          </span>
                        )}
                      </span>
                      <span className={styles.packageMeta}>
                        اعتبار: {pkg.duration}
                      </span>
                      <span className={styles.packageBottom}>
                        <strong className={styles.packageCost}>
                          {formatNumber(Number(pkg.price))} تومان
                        </strong>
                        {pkg.perInquiryPrice && (
                          <span className={styles.packageUnit}>
                            هر استعلام{" "}
                            {formatNumber(Number(pkg.perInquiryPrice))}
                          </span>
                        )}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
            <button
              className={styles.resetOrder}
              type="button"
              onClick={() => {
                setSelectedPackage(defaultValue);
                setIsPackagesOpen(false);
              }}
            >
              فقط یک استعلام می‌خواهم
            </button>
          </div>
        )}
      </div>
    </form>
  );
};

export default RadioPackageGroup;
